import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { Generic, GenericUser } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { DirectieSeriiService } from '../../directie-serii.service';
import { AssignedUserPayload } from '../../directie-serii.interface';


@Component({
    selector: 'app-add-alocare-responsabile-compartiment',
    templateUrl: './add-alocare-responsabile-compartiment.component.html',
    styleUrls: ['./add-alocare-responsabile-compartiment.component.scss']
})
export class AddAlocareResponsabileCompartimentComponent {
    // general error
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;
    helper = helper;
    token!: string;
    isLoading = false;

    dgaspc_id = environment.interop.dgaspcId;

    form: FormGroup = new FormGroup({
        directie_id: new FormControl(this.data.directie_id, [Validators.required, Validators.min(0)]),
        compartiment_id: new FormControl(this.data.compartiment_id),
        user_id: new FormControl(null, [Validators.required, Validators.min(1)]),
    });

    defaultParameter: { name: string; id: string } = {
        name: '--Alegeți--',
        id: '-1',
    };

    private destroy$ = new Subject<void>();

    directiiActive: Generic[] = [];
    filteredDirectii: Generic[] = [];
    directiiControl = new FormControl();

    compartimenteActive: Generic[] = [];
    filteredCompartimente: Generic[] = [];
    compartimenteControl = new FormControl();

    usersActive: Generic[] = [];
    filteredUsers: Generic[] = [];
    usersControl = new FormControl();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: { directie_id: number, compartiment_id: number | null },
        private dialogRef: MatDialogRef<AddAlocareResponsabileCompartimentComponent>,
        private directieSeriiService: DirectieSeriiService,
        private notificationService: NotificationService,
        private commonService: CommonService,
    ) {
        // console.log('directie_id', this.data.directie_id, 'compartiment_id', this.data.compartiment_id)
    }

    ngOnInit() {
        this.loadDirectiiActive();

        this.loadCompartimenteActive(this.dgaspc_id);

        this.loadUsers(this.data.directie_id, this.data.compartiment_id);

        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    submitForm() {
        this.isLoading = true;

        const {
            directie_id,
            compartiment_id,
            user_id,
        } = this.form.value;

        const payload: AssignedUserPayload = {
            directie_id: directie_id,
            compartiment_id: compartiment_id && compartiment_id > 0 ? compartiment_id : null,
            user_id: user_id,
        };

        this.directieSeriiService
            .addAssignedUser(payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    loadDirectiiActive() {
        this.isLoading = true;
        this.commonService
            .getDirectiiActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.directiiActive = results;
                    this.filteredDirectii = [...this.directiiActive];
                },
                error: (error) => {
                    console.error('Error loading directii', error);
                    this.filteredDirectii = [];
                },
            });
    }

    onDirectieClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].patchValue(-1);

        if (directieId > 0) {
            this.loadCompartimenteActive(directieId);
            this.loadUsers(directieId, null);
        } else {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.usersActive = [];
            this.filteredUsers = [];
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;

        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        compartimenteObservable$
            .pipe(
                map((results: any) => [...results]),
                finalize(() => { this.isLoading = false })
            ).subscribe({
                next: (data) => {
                    this.compartimenteActive = [...data];
                    this.filteredCompartimente = [...this.compartimenteActive];
                },
                error: (error) => {
                    console.error(error);
                    this.filteredCompartimente = [];
                },
            });
    }

    onCompartimentClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number): void {
        const directieId = this.form.get('directie_id')?.value;

        if (compartimentId > 0) {
            this.loadUsers(directieId, compartimentId);
        } else {
            this.loadUsers(directieId, null);
        }
    }

    loadUsers(directieId?: number | null, compartimentId?: number | null) {
        this.isLoading = true;

        const payload: any = {
            view_all : 1
        };
        if (directieId && directieId > 0) {
            payload.directie_id = directieId;
        }
        if (compartimentId && compartimentId > 0) {
            payload.compartiment_id = compartimentId;
        }

        this.commonService.getUsersByOrganigrama(payload)
            .pipe(
                map((results: any) => [...results]),
                finalize(() => { this.isLoading = false })
            ).subscribe({
                next: (data) => {
                    console.log('Loaded users:', data);
                    this.usersActive = [...data];
                    this.filteredUsers = [...this.usersActive];
                },
                error: (error) => {
                    console.error(error);
                    this.filteredUsers = [];
                },
            });
    }

    onUserClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.form.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    filterUsers(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        console.log('Filtering users with term:', this.usersActive);
        const filtered = this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }
}
